import { STRINGS } from '@/constants/strings';
import { enqueueWebAlert } from '@/components/ui/alert-web-dialog';
import { Alert, Platform, type AlertButton } from 'react-native';

type AlertMessage = string;

type BaseAlertOptions = {
  title?: string;
  message: AlertMessage;
  buttons?: AlertButton[];
};

type ConfirmOptions = {
  title?: string;
  message: AlertMessage;
  confirmText?: string;
  cancelText?: string;
  /** iOS: `destructive` shows the confirm action in red (e.g. permanent delete). */
  confirmStyle?: 'default' | 'destructive';
};

const isWeb = Platform.OS === 'web';

export const showAlert = ({ title, message, buttons }: BaseAlertOptions) => {
  if (isWeb) {
    enqueueWebAlert({
      title: title ?? '',
      message,
      buttons: buttons ?? [{ text: STRINGS.dismiss }],
      cancelable: false,
    });
    return;
  }
  Alert.alert(title ?? '', message, buttons ?? [{ text: STRINGS.dismiss }], {
    cancelable: false,
  });
};

export const showInfoAlert = (message: AlertMessage, title = STRINGS.common.alert.info) => {
  if (isWeb) {
    enqueueWebAlert({
      title,
      message,
      buttons: [{ text: STRINGS.dismiss }],
      cancelable: false,
    });
    return;
  }
  Alert.alert(title, message, [{ text: STRINGS.dismiss }], { cancelable: false });
};

export const showErrorAlert = (message: AlertMessage, title = STRINGS.common.alert.error) => {
  if (isWeb) {
    enqueueWebAlert({
      title,
      message,
      buttons: [{ text: STRINGS.dismiss, style: 'destructive' }],
      cancelable: true,
    });
    return;
  }
  Alert.alert(title, message, [{ text: STRINGS.dismiss, style: 'destructive' }], {
    cancelable: true,
  });
};

export const showSuccessAlert = (message: AlertMessage, title = STRINGS.common.alert.success) => {
  if (isWeb) {
    enqueueWebAlert({
      title,
      message,
      buttons: [{ text: STRINGS.dismiss }],
      cancelable: false,
    });
    return;
  }
  Alert.alert(title, message, [{ text: STRINGS.dismiss }], { cancelable: false });
};

export const showConfirm = ({
  title = STRINGS.common.alert.confirmTitle,
  message,
  confirmText = STRINGS.common.alert.confirm,
  cancelText = STRINGS.common.alert.cancel,
  confirmStyle = 'default',
}: ConfirmOptions): Promise<boolean> => {
  if (isWeb) {
    return new Promise(resolve => {
      enqueueWebAlert({
        title,
        message,
        cancelable: true,
        buttons: [
          { text: cancelText, style: 'cancel', onPress: () => resolve(false) },
          { text: confirmText, style: confirmStyle, onPress: () => resolve(true) },
        ],
      });
    });
  }
  return new Promise(resolve => {
    Alert.alert(
      title,
      message,
      [
        {
          text: cancelText,
          style: 'cancel',
          onPress: () => resolve(false),
        },
        {
          text: confirmText,
          style: confirmStyle,
          onPress: () => resolve(true),
        },
      ],
      { cancelable: true },
    );
  });
};
